import { useEffect, useRef } from "react"
import { cn } from "@shared/lib/utils"
import type { DataTableProps } from "@shared/types/data-table"
import { LoadingSkeleton } from "./LoadingSkeleton"
import { EmptyState, getEmptyStateConfig } from "./EmptyState"
import { ErrorState } from "@shared/components/ui/ErrorState"
import { Inbox } from "lucide-react"
import { Pagination } from "./Pagination"
import { DataTableContainer } from "./components/DataTableContainer"
import { useDataTableSelection } from "@shared/components/ui/data-table/hooks/useDataTableSelection"

export function DataTable<T extends object>({
  data,
  columns,
  selectedItems,
  onSelectionChange,
  getItemId,
  onRowClick,
  rowActions,
  hoverActions,
  pagination,
  loading = false,
  loadingRows = 10,
  emptyStateEntityType,
  emptyStateActions,
  className,
  unavailableItems = [],
  getItemStatus,
  allowedStatuses = [],
  onSelectSucceededOnly,
  activeItemId,
  tableAriaLabel,
  getRowClassName,
  error,
  onRetry,
}: DataTableProps<T>) {
  
  const containerRef = useRef<HTMLDivElement>(null)
  const hasSelection = Boolean(onSelectionChange)
  const hasActions = Boolean(rowActions || onRowClick)
  const hasData = Boolean(data?.length)

  const { 
    toggleItemSelection, 
    toggleAllSelection, 
    selectSucceededOnly, 
    selectionState 
  } = useDataTableSelection({
    paginatedData: data || [],
    selectedItems: selectedItems || [],
    onSelectionChange,
    getItemId,
    unavailableItems,
    getItemStatus,
    allowedStatuses,
  })

  // Handle selectSucceededOnly callback with proper cleanup
  useEffect(() => {
    if (onSelectSucceededOnly && selectSucceededOnly) {
      onSelectSucceededOnly(selectSucceededOnly)
    }
  }, [onSelectSucceededOnly, selectSucceededOnly])

  if (loading) {
    return (
      <LoadingSkeleton
        columns={columns.length}
        rows={loadingRows}
        hasActions={hasActions}
        hasSelection={hasSelection}
        showPagination={!!pagination}
      />
    )
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error.message
    return (
      <div className={cn("relative w-full", className)}>
        <ErrorState
          variant="server"
          title="Failed to load data"
          description={errorMessage}
          onRetry={onRetry}
          inline
        />
      </div>
    )
  }

  if (!hasData) {
    if (emptyStateEntityType) {
      const config = getEmptyStateConfig(emptyStateEntityType, emptyStateActions)
      return (
        <div className={cn("relative w-full", className)}>
          <EmptyState {...config} />
        </div>
      )
    }
    
    return (
      <div className={cn("relative w-full", className)}>
        <EmptyState 
          title="No results found" 
          description="Try adjusting your filters or search criteria." 
          icon={Inbox}
          actions={emptyStateActions}
        />
      </div>
    )
  }

  return (
    <DataTableContainer
      data={data}
      columns={columns}
      selectedItems={selectedItems || []}
      unavailableItems={unavailableItems}
      rowActions={rowActions}
      hoverActions={hoverActions}
      onRowClick={onRowClick}
      onToggleSelection={toggleItemSelection}
      onToggleAllSelection={toggleAllSelection}
      getItemId={getItemId}
      getItemStatus={getItemStatus}
      allowedStatuses={allowedStatuses}
      selectionState={selectionState}
      loading={loading}
      activeItemId={activeItemId}
      className={className}
      tableAriaLabel={tableAriaLabel}
      getRowClassName={getRowClassName}
      ref={containerRef}
    >
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          loading={loading}
          onPageChange={pagination.onPageChange}
        />
      )}
    </DataTableContainer>
  )
}
